<template>
    <div>
        <div class="mb-3">
            <label class="mb-1 block text-xs font-semibold text-theme-toolbar-icon">
                {{ localizations[lang].flashcard.front }}
            </label>
            <textarea
                ref="frontInput"
                v-model="frontText"
                class="w-full resize-y rounded-lg border border-theme-border bg-theme-bg p-2 text-sm text-theme-text outline-none focus:border-theme-accent"
                :placeholder="localizations[lang].flashcard.frontPlaceholder"
                rows="2"
            ></textarea>
        </div>

        <div class="relative my-3 flex items-center justify-center">
            <div class="absolute inset-x-0 top-1/2 h-px bg-theme-border"></div>
            <button
                v-tooltip.top="localizations[lang].flashcard.swapFrontBack"
                type="button"
                class="relative z-10 flex h-7 w-7 items-center justify-center rounded-md border border-theme-border bg-theme-bg text-theme-toolbar-icon transition-colors hover:border-theme-accent hover:text-theme-accent"
                :aria-label="localizations[lang].flashcard.swapFrontBack"
                @click="swapFrontBack"
            >
                <svg-icon icon="swap-vert-24px" width="14px" height="14px" />
            </button>
        </div>

        <div class="mb-3">
            <label class="mb-1 block text-xs font-semibold text-theme-toolbar-icon">
                {{ localizations[lang].flashcard.back }}
            </label>
            <textarea
                v-model="backText"
                class="w-full resize-y rounded-lg border border-theme-border bg-theme-bg p-2 text-sm text-theme-text outline-none focus:border-theme-accent"
                :placeholder="localizations[lang].flashcard.backPlaceholder"
                rows="3"
            ></textarea>
        </div>

        <div class="mb-3">
            <HighlighterColorPicker variant="standard" :selected-slug="selectedColorSlug" @select="handleColorSelect" />
        </div>

        <div class="mt-4 flex gap-2">
            <button
                type="button"
                class="flex-1 rounded-lg border border-theme-border bg-theme-bg py-2 text-sm font-medium text-theme-text transition-opacity hover:opacity-80"
                @click="$emit('cancel')"
            >
                {{ localizations[lang].flashcard.cancel }}
            </button>
            <button
                type="button"
                class="flex-1 rounded-lg bg-theme-accent py-2 text-sm font-medium text-theme-accent-on transition-opacity hover:opacity-90 disabled:cursor-default disabled:opacity-40"
                :disabled="!canSave"
                @click="save"
            >
                {{ localizations[lang].flashcard.save }}
            </button>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';
import HighlighterColorPicker from '../HighlighterColorPicker/HighlighterColorPicker.vue';

export default {
    name: 'FlashcardForm',
    components: {
        HighlighterColorPicker,
    },
    props: {
        editCard: { type: Object, default: null },
        initialBackText: { type: String, default: '' },
    },
    data() {
        return {
            frontText: this.editCard ? this.editCard.frontText : '',
            backText: this.editCard ? this.editCard.backText : this.initialBackText,
            selectedColorSlug: this.editCard ? this.editCard.colorSlug : '',
            selectedColor: this.editCard ? this.editCard.color : '',
        };
    },
    computed: {
        ...mapState(['localizations', 'lang', 'flashcardModel']),
        ...mapGetters(['allHighlighters']),
        /**
         * @returns {boolean} Whether the form has enough data to save
         */
        canSave() {
            return (
                this.frontText.trim().length > 0 && this.backText.trim().length > 0 && this.selectedColorSlug.length > 0
            );
        },
    },
    created() {
        if (!this.selectedColorSlug) {
            this.applyDefaultColor();
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (this.$refs.frontInput) {
                this.$refs.frontInput.focus();
            }
        });
    },
    methods: {
        ...mapMutations(['openToast']),
        /**
         * Selects the first available highlighter color as the default.
         */
        applyDefaultColor() {
            const defaultColor = this.allHighlighters[0];
            if (defaultColor) {
                this.selectedColorSlug = defaultColor.slug;
                this.selectedColor = defaultColor.color;
            }
        },
        /**
         * Handles a color selection from the color picker.
         * @param {{ slug: string, color: string }} selected - The chosen color
         */
        handleColorSelect({ slug, color }) {
            this.selectedColorSlug = slug;
            this.selectedColor = color;
        },
        /**
         * Swaps the front and back text fields.
         */
        swapFrontBack() {
            const tempText = this.frontText;
            this.frontText = this.backText;
            this.backText = tempText;
        },
        /**
         * Saves the flashcard (create or update) through the flashcard model.
         */
        async save() {
            if (!this.canSave) return;
            const flashcard = {
                ...this.flashcardModel,
                ...(this.editCard || {}),
                frontText: this.frontText.trim(),
                backText: this.backText.trim(),
                color: this.selectedColor,
                colorSlug: this.selectedColorSlug,
            };
            if (this.editCard) {
                const colorChanged = this.editCard.color !== this.selectedColor;
                const updated = await flashcard.update();
                if (!updated) {
                    this.openToast({ text: this.localizations[this.lang].flashcard.updateFailed });
                    return;
                }
                if (colorChanged) this.$bus.$emit('flashcardMarkerUpdated', flashcard);
                this.openToast({ text: this.localizations[this.lang].flashcard.updated });
                this.$emit('saved');
                return;
            }
            flashcard.create();
            const stored = await flashcard.store();
            if (!stored) {
                this.openToast({ text: this.localizations[this.lang].flashcard.createFailed });
                return;
            }
            this.$bus.$emit('flashcardMarkerCreated', flashcard);
            this.openToast({ text: this.localizations[this.lang].flashcard.created });
            this.$emit('saved');
        },
    },
};
</script>
